import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import PageContainer from '../PageContainer/PageContainer';
import LoginModal from '../LoginModal/LoginModal';
import { hasPendingSocialAuthError } from '../../utils/socialAuth';
import restaurantService, { PlanFeaturesPayload } from '../../services/restaurantService';
import queueService from '../../services/queueService';
import { hasCustomerFeature } from '../../utils/planFeatures';
import { trackEvent } from '../../utils/analytics';
import { useAppSelector } from '../../store/hooks';
import './QueueJoin.css';

const QueueJoin: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, authReady } = useAppSelector((state) => state.user);

  const [restaurantName, setRestaurantName] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [guestName, setGuestName] = useState(user?.userName || '');
  const [guestPhone, setGuestPhone] = useState(user?.phoneNumber || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [featureBlocked, setFeatureBlocked] = useState<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<{ label: string; publicRef: string } | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(() => hasPendingSocialAuthError());
  const pendingJoinRef = useRef(false);

  const phoneFromLogin = isAuthenticated && !!user?.phoneNumber;

  const syncGuestFromUser = useCallback(() => {
    if (user?.userName) {
      setGuestName(user.userName);
    }
    if (user?.phoneNumber) {
      setGuestPhone(user.phoneNumber);
    }
  }, [user]);

  const loadRestaurant = useCallback(async () => {
    if (!appName) return;
    try {
      const res = await restaurantService.getRestaurant(appName);
      if (res.success && res.restaurant) {
        setRestaurantName(res.restaurant.restaurantName || appName);
      }
      const features: PlanFeaturesPayload | undefined = res.features;
      if (!hasCustomerFeature(features, 'queue_guest_join')) {
        setFeatureBlocked('Guest queue self-join is not available at this restaurant.');
      }
    } catch {
      setFeatureBlocked('Could not load restaurant details.');
    }
  }, [appName]);

  useEffect(() => {
    loadRestaurant();
  }, [loadRestaurant]);

  useEffect(() => {
    syncGuestFromUser();
  }, [syncGuestFromUser]);

  useEffect(() => {
    if (issuedToken) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [issuedToken]);

  const adjustPartySize = (delta: number) => {
    setPartySize((prev) => Math.min(20, Math.max(1, prev + delta)));
  };

  const submitJoin = useCallback(async () => {
    if (!appName) return;
    const phone = (phoneFromLogin ? user?.phoneNumber : guestPhone)?.trim() || '';
    if (!phone) {
      setErrorMessage('Phone number is required.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const res = await queueService.joinQueue(appName, {
        partySize,
        guestName: guestName.trim() || undefined,
        guestPhone: phone,
        notes: notes.trim() || undefined,
      });
      if (res.success && res.token?.publicRef) {
        trackEvent('join_queue', { restaurant_app: appName, party_size: partySize });
        setIssuedToken({
          label: res.token.tokenLabel,
          publicRef: res.token.publicRef,
        });
      } else {
        setErrorMessage(res.message || 'Could not join the waitlist.');
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string; requiresLogin?: boolean } };
      };
      const status = axiosErr.response?.status;
      const data = axiosErr.response?.data;
      if (status === 401 || data?.requiresLogin) {
        pendingJoinRef.current = true;
        setShowLoginModal(true);
        return;
      }
      setErrorMessage(data?.message || 'Could not join the waitlist.');
    } finally {
      setLoading(false);
    }
  }, [appName, guestName, guestPhone, notes, partySize, phoneFromLogin, user?.phoneNumber]);

  useEffect(() => {
    if (!isAuthenticated || !user || !pendingJoinRef.current) {
      return;
    }
    pendingJoinRef.current = false;
    const timer = window.setTimeout(() => {
      submitJoin();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, user, submitJoin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appName || !authReady) return;

    if (!isAuthenticated) {
      pendingJoinRef.current = true;
      setShowLoginModal(true);
      return;
    }

    await submitJoin();
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    syncGuestFromUser();
  };

  const handleLoginModalClose = () => {
    pendingJoinRef.current = false;
    setShowLoginModal(false);
  };

  if (featureBlocked) {
    return (
      <div className="queue-join-page">
        <Header />
        <PageContainer className="queue-join-container">
          <div className="queue-join-card queue-join-card--blocked">
            <div className="queue-join-icon queue-join-icon--muted" aria-hidden="true">
              <i className="fa fa-clock-o" />
            </div>
            <h2>Waitlist unavailable</h2>
            <p>{featureBlocked}</p>
            <Link to={`/restaurants/${appName}`} className="queue-join-btn queue-join-btn--secondary">
              Back to restaurant
            </Link>
          </div>
        </PageContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="queue-join-page">
      <Header />
      <PageContainer className="queue-join-container">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: restaurantName || appName || 'Restaurant', path: `/restaurants/${appName}` },
            { label: 'Join waitlist' },
          ]}
        />

        <div className="queue-join-layout">
          <div className="queue-join-card">
            {issuedToken ? (
              <div className="queue-join-success">
                <div className="queue-join-success__badge">
                  <i className="fa fa-check" aria-hidden="true" />
                  You&apos;re on the list
                </div>
                <div className="queue-join-ticket">
                  <span className="queue-join-ticket__label">Your token</span>
                  <div className="queue-join-ticket__number">{issuedToken.label}</div>
                  <p className="queue-join-ticket__hint">
                    Keep this page open or track your token anytime. We&apos;ll text you when your table is ready.
                  </p>
                </div>
                <button
                  type="button"
                  className="queue-join-btn"
                  onClick={() => navigate(`/restaurants/${appName}/queue/${issuedToken.publicRef}`)}
                >
                  Track my token
                </button>
              </div>
            ) : (
              <>
                <div className="queue-join-hero">
                  <div className="queue-join-icon" aria-hidden="true">
                    <i className="fa fa-ticket" />
                  </div>
                  <div>
                    <p className="queue-join-eyebrow">{restaurantName || appName}</p>
                    <h1>Join the waitlist</h1>
                    <p className="queue-join-subtitle">
                      Get a token in seconds — no app download. We&apos;ll notify you when your table is ready.
                    </p>
                  </div>
                </div>

                <ul className="queue-join-perks">
                  <li><i className="fa fa-mobile" aria-hidden="true" /> SMS updates when ready</li>
                  <li><i className="fa fa-qrcode" aria-hidden="true" /> Track your place live</li>
                </ul>

                <form className="queue-join-form" onSubmit={handleSubmit}>
                  <div className="queue-join-field">
                    <label htmlFor="partySize">Party size</label>
                    <div className="queue-join-stepper">
                      <button
                        type="button"
                        className="queue-join-stepper__btn"
                        onClick={() => adjustPartySize(-1)}
                        disabled={partySize <= 1}
                        aria-label="Decrease party size"
                      >
                        −
                      </button>
                      <span className="queue-join-stepper__value" id="partySize" aria-live="polite">
                        {partySize}
                        <small>{partySize === 1 ? 'guest' : 'guests'}</small>
                      </span>
                      <button
                        type="button"
                        className="queue-join-stepper__btn"
                        onClick={() => adjustPartySize(1)}
                        disabled={partySize >= 20}
                        aria-label="Increase party size"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="queue-join-field">
                    <label htmlFor="guestName">Your name</label>
                    <input
                      id="guestName"
                      type="text"
                      className="queue-join-input"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Optional"
                      autoComplete="name"
                    />
                  </div>

                  <div className="queue-join-field">
                    <label htmlFor="guestPhone">
                      Phone number <span className="queue-join-required">*</span>
                    </label>
                    <input
                      id="guestPhone"
                      type="tel"
                      className="queue-join-input"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="10-digit mobile number"
                      autoComplete="tel"
                      required={!phoneFromLogin}
                      disabled={phoneFromLogin}
                      readOnly={phoneFromLogin}
                    />
                    {phoneFromLogin && (
                      <p className="queue-join-field-hint">Phone number from your login account.</p>
                    )}
                  </div>

                  <div className="queue-join-field">
                    <label htmlFor="notes">Special requests</label>
                    <input
                      id="notes"
                      type="text"
                      className="queue-join-input"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="High chair, outdoor seating, etc."
                    />
                  </div>

                  {errorMessage && (
                    <div className="queue-join-error" role="alert">
                      <i className="fa fa-exclamation-circle" aria-hidden="true" />
                      {errorMessage}
                    </div>
                  )}

                  <button type="submit" className="queue-join-btn" disabled={loading || !authReady}>
                    {loading ? 'Joining waitlist…' : 'Get my token'}
                  </button>
                </form>
              </>
            )}
          </div>

          {!issuedToken && (
            <aside className="queue-join-aside">
              <h3>How it works</h3>
              <ol>
                <li>
                  <strong>Get your token</strong>
                  <span>We assign you a number in line instantly.</span>
                </li>
                <li>
                  <strong>Wait comfortably</strong>
                  <span>Browse the menu or step out — we&apos;ll text you.</span>
                </li>
                <li>
                  <strong>Come when called</strong>
                  <span>Head to the host stand when your token is ready.</span>
                </li>
              </ol>
            </aside>
          )}
        </div>
      </PageContainer>
      <Footer />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleLoginModalClose}
        title="Sign in to join the waitlist"
        message="Sign in with your phone number to get your queue token."
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default QueueJoin;
